import api from './api'

export interface ErrorLogEntry {
  message: string
  stack?: string
  componentName?: string
  info?: string
  url: string
  userAgent: string
  timestamp: Date
  userId?: string
}

class ErrorService {
  /**
   * Log error to backend API
   */
  async logError(error: Error, componentName?: string, info?: string): Promise<void> {
    try {
      const errorLog: ErrorLogEntry = {
        message: error.message,
        stack: error.stack,
        componentName,
        info,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date()
      }

      // Log to console in development
      if (import.meta.env.DEV) {
        console.error('🔴 Error logged:', errorLog)
      }

      // Send to backend (don't await to avoid blocking)
      api.post('/errors/log', errorLog).catch((err) => {
        // Silently fail - we don't want error logging to cause more errors
        console.error('Failed to log error to backend:', err)
      })
    } catch (err) {
      // Silently fail - we don't want error logging to cause more errors
      console.error('Error in error logging service:', err)
    }
  }

  /**
   * Log warning to backend API
   */
  async logWarning(message: string, context?: Record<string, unknown>): Promise<void> {
    try {
      if (import.meta.env.DEV) {
        console.warn('⚠️ Warning logged:', message, context)
      }

      api.post('/errors/log-warning', { message, context, timestamp: new Date() }).catch((err) => {
        console.error('Failed to log warning to backend:', err)
      })
    } catch (err) {
      console.error('Error in warning logging service:', err)
    }
  }

  /**
   * Get user-friendly error message
   */
  getUserFriendlyMessage(error: Error): string {
    // Network errors
    if (error.message.includes('Network Error') || error.message.includes('ECONNREFUSED')) {
      return 'Unable to connect to the server. Please check your internet connection and try again.'
    }

    // Timeout errors
    if (error.message.includes('timeout')) {
      return 'The request took too long to complete. Please try again.'
    }

    // Generic error
    return 'An unexpected error occurred. Our team has been notified and is working to fix it.'
  }

  /**
   * Check if error should be reported to user
   */
  shouldReportToUser(error: any): boolean {
    // Don't report validation errors (they're handled by forms)
    if (error.message?.includes('validation')) {
      return false
    }

    // Don't report HTTP client/auth errors (handled by components or auth interceptor)
    const status = error.response?.status
    if (status && status >= 400 && status < 500) {
      return false
    }
    if (error.message?.includes('401') || error.message?.includes('403')) {
      return false
    }

    return true
  }
}

export const errorService = new ErrorService()
export default errorService
